import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort, formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import {
  emptyDenominationCounts,
  hasDenominationCounts,
  normalizeDenominationCounts,
  sumDenominationCounts,
  type CashDenominationCounts,
  EUR_CASH_DENOMINATIONS,
  denominationKey,
} from "@/lib/cashDenominations";
import { formatCurrency, toAmountNumber } from "@/lib/money";
import { CashDenominationGrid } from "@/pages/daily-closing/CashDenominationGrid";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  paymentMethodLabel,
} from "@/lib/paymentMethod";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Table } from "@/components/ui/Table";
import type {
  CashMovement,
  CashMovementCreatePayload,
  CashMovementType,
  DailyClosing,
  DailyClosingCreatePayload,
  DailyClosingListResponse,
  DailyClosingPreview,
} from "@/types/camping";

const CASH_MOVEMENT_TYPES: { value: CashMovementType; label: string }[] = [
  { value: "deposit", label: "Einlage (Wechselgeld)" },
  { value: "withdrawal", label: "Entnahme" },
  { value: "private_deposit", label: "Privateinlage" },
  { value: "private_withdrawal", label: "Privatentnahme" },
  { value: "expense", label: "Betriebsausgabe (Bar)" },
];

const MOVEMENT_TYPE_LABELS: Record<CashMovementType, string> = {
  deposit: "Einlage",
  withdrawal: "Entnahme",
  private_deposit: "Privateinlage",
  private_withdrawal: "Privatentnahme",
  expense: "Betriebsausgabe",
};

function csvEscape(value: string): string {
  if (value.includes('"') || value.includes(";") || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(filename: string, rows: string[][]) {
  const bom = "\uFEFF";
  const body = rows.map((row) => row.map(csvEscape).join(";")).join("\n");
  const blob = new Blob([bom + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function shiftIsoDate(iso: string, days: number): string {
  const [y = 0, m = 1, d = 1] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function formatLongDay(iso: string): string {
  const [y = 0, m = 1, d = 1] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(y, m - 1, d));
}

function amountsDiffer(diff: number | null): boolean {
  return diff != null && Math.abs(diff) > 0.001;
}

function cashDiffSentence(diff: number | null, currency: string, counted: boolean): string {
  if (!counted || diff == null) return "Bitte Scheine und Münzen zählen.";
  const amount = formatCurrency(Math.abs(diff), currency);
  if (diff < -0.001) return `In der Kasse fehlen ${amount}.`;
  if (diff > 0.001) return `In der Kasse sind ${amount} zu viel.`;
  return "Die Kasse stimmt mit dem Soll überein.";
}

function cardDiffSentence(diff: number | null, currency: string, entered: boolean): string {
  if (!entered || diff == null) return "Betrag vom Terminal-Beleg eintragen.";
  const amount = formatCurrency(Math.abs(diff), currency);
  if (diff < -0.001) return `Das Terminal liegt ${amount} unter dem System.`;
  if (diff > 0.001) return `Das Terminal liegt ${amount} über dem System.`;
  return "Stimmt. Bitte kurz mit dem Beleg gegenprüfen.";
}

function LedgerLine({
  label,
  value,
  currency,
  emphasis,
}: {
  label: string;
  value: string;
  currency: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex items-baseline justify-between gap-3",
        emphasis ? "mt-1 border-t border-separator pt-2.5" : "py-1.5",
      )}
    >
      <span className={clsx("text-sm", emphasis ? "font-semibold text-label" : "text-label-secondary")}>
        {label}
      </span>
      <span
        className={clsx(
          "tabular-nums text-label",
          emphasis ? "text-base font-semibold" : "text-sm font-medium",
        )}
      >
        {formatCurrency(value, currency)}
      </span>
    </div>
  );
}

export function DailyClosingPage() {
  const { setPageMeta } = usePageHeader();
  const today = todayLocal();

  const [closingDate, setClosingDate] = useState(today);
  const [preview, setPreview] = useState<DailyClosingPreview | null>(null);
  const [history, setHistory] = useState<DailyClosing[]>([]);
  const [selectedClosing, setSelectedClosing] = useState<DailyClosing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [cashCounted, setCashCounted] = useState("");
  const [denominationCounts, setDenominationCounts] = useState<CashDenominationCounts>(
    emptyDenominationCounts,
  );
  const [useManualCashEntry, setUseManualCashEntry] = useState(false);
  const [cardSettlement, setCardSettlement] = useState("");
  const [cardReference, setCardReference] = useState("");
  const [notes, setNotes] = useState("");
  const [differenceReason, setDifferenceReason] = useState("");
  const [closing, setClosing] = useState(false);
  const [movementFormOpen, setMovementFormOpen] = useState(false);

  const [movementType, setMovementType] = useState<CashMovementType>("deposit");
  const [movementAmount, setMovementAmount] = useState("");
  const [movementReason, setMovementReason] = useState("");
  const [movementReference, setMovementReference] = useState("");
  const [addingMovement, setAddingMovement] = useState(false);
  const formDateRef = useRef<string | null>(null);

  const currency = preview?.currency ?? "EUR";

  const denominationTotal = useMemo(
    () => sumDenominationCounts(denominationCounts),
    [denominationCounts],
  );

  const cashDifference = useMemo(() => {
    const counted = toAmountNumber(cashCounted);
    const expected = toAmountNumber(preview?.cash_expected);
    if (counted == null || expected == null) return null;
    return counted - expected;
  }, [cashCounted, preview?.cash_expected]);

  const cardDifference = useMemo(() => {
    const settlement = toAmountNumber(cardSettlement);
    const expected = toAmountNumber(preview?.card_expected);
    if (settlement == null || expected == null) return null;
    return settlement - expected;
  }, [cardSettlement, preview?.card_expected]);

  const hasDifference = useMemo(() => {
    if (amountsDiffer(cashDifference)) return true;
    if (amountsDiffer(cardDifference)) return true;
    return false;
  }, [cashDifference, cardDifference]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ closing_date: closingDate });
    Promise.all([
      api.get<DailyClosingPreview>(`/api/v1/daily-closings/preview?${params}`),
      api.get<DailyClosingListResponse>(
        `/api/v1/daily-closings?from_date=${closingDate}&to_date=${closingDate}&limit=1`,
      ),
      api.get<DailyClosingListResponse>(
        `/api/v1/daily-closings?limit=10&offset=0`,
      ),
    ])
      .then(([previewData, dayClosings, recentClosings]) => {
        setPreview(previewData);
        setHistory(recentClosings.items);
        const closed = dayClosings.items[0];
        const resetOpenForm = formDateRef.current !== previewData.closing_date;
        formDateRef.current = previewData.closing_date;
        if (previewData.is_closed && closed) {
          setSelectedClosing(closed);
          setCashCounted(closed.cash_counted);
          const savedCounts = normalizeDenominationCounts(
            closed.snapshot?.cash_denomination_counts as Record<string, unknown> | undefined,
          );
          setDenominationCounts(savedCounts);
          setUseManualCashEntry(!hasDenominationCounts(savedCounts));
          setCardSettlement(closed.card_settlement ?? "");
          setCardReference(closed.card_settlement_reference ?? "");
          setNotes(closed.notes ?? "");
          setDifferenceReason(closed.difference_reason ?? "");
        } else if (resetOpenForm) {
          setSelectedClosing(null);
          setCashCounted("");
          setDenominationCounts(emptyDenominationCounts());
          setUseManualCashEntry(false);
          const cardExpected = toAmountNumber(previewData.card_expected);
          setCardSettlement(cardExpected === 0 ? "0" : "");
          setCardReference("");
          setNotes("");
          setDifferenceReason("");
          setMovementFormOpen(false);
        } else {
          setSelectedClosing(null);
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [closingDate]);

  useEffect(() => {
    setPageMeta({
      title: "Tagesabschluss",
      subtitle: "Kasse zählen, Karte prüfen, Tag festschreiben",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!useManualCashEntry) {
      setCashCounted(denominationTotal > 0 ? String(denominationTotal) : "");
    }
  }, [denominationTotal, useManualCashEntry]);

  function selectDate(next: string) {
    setSuccess(null);
    setClosingDate(next);
  }

  function handleDenominationCountChange(key: string, count: number) {
    setDenominationCounts((prev) => ({ ...prev, [key]: count }));
  }

  async function handleAddMovement() {
    if (!movementAmount || !movementReason.trim()) return;
    setAddingMovement(true);
    setError(null);
    try {
      const payload: CashMovementCreatePayload = {
        movement_date: closingDate,
        movement_type: movementType,
        amount: movementAmount,
        reason: movementReason.trim(),
        reference: movementReference.trim() || undefined,
      };
      await api.post<CashMovement>("/api/v1/cash-movements", payload);
      setMovementAmount("");
      setMovementReason("");
      setMovementReference("");
      setSuccess("Bargeldbewegung erfasst.");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Erfassen.");
    } finally {
      setAddingMovement(false);
    }
  }

  async function handleClose(e: React.FormEvent) {
    e.preventDefault();
    if (!cashCounted) return;
    if (hasDifference && !differenceReason.trim()) {
      setError("Bei Kassen- oder Kartenabweichung ist eine Begründung erforderlich.");
      return;
    }
    setClosing(true);
    setError(null);
    try {
      const payload: DailyClosingCreatePayload = {
        closing_date: closingDate,
        cash_counted: cashCounted,
        notes: notes.trim() || undefined,
        difference_reason: differenceReason.trim() || undefined,
        card_settlement_reference: cardReference.trim() || undefined,
      };
      if (!useManualCashEntry && hasDenominationCounts(denominationCounts)) {
        payload.cash_denomination_counts = denominationCounts;
      }
      if (cardSettlement) {
        payload.card_settlement = cardSettlement;
      }
      const result = await api.post<DailyClosing>("/api/v1/daily-closings", payload);
      setSelectedClosing(result);
      setSuccess(`Tagesabschluss für ${formatIsoDateShort(closingDate)} festgeschrieben.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Festschreiben.");
    } finally {
      setClosing(false);
    }
  }

  function exportClosingCsv() {
    if (!preview) return;
    const closingRow = selectedClosing;
    const rows: string[][] = [
      ["Tagesabschluss", closingDate],
      ["Anfangsbestand (Vortag)", preview.opening_balance],
      ["Barzahlungen", preview.cash_receipts],
      ["Einlagen", preview.cash_deposits],
      ["Entnahmen/Ausgaben", preview.cash_withdrawals],
      ["Soll-Bestand", preview.cash_expected],
      ["Ist-Bestand (gezählt)", closingRow?.cash_counted ?? cashCounted],
      ["Kassendifferenz", closingRow?.cash_difference ?? String(cashDifference ?? "")],
      ["Kartenzahlungen (System)", preview.card_expected],
      ["Kartenabrechnung (Terminal)", closingRow?.card_settlement ?? cardSettlement],
      ["Kartenabweichung", closingRow?.card_difference ?? String(cardDifference ?? "")],
      ["Überweisungen", preview.transfer_total],
      ["Sonstige", preview.other_total],
      ["Zahlungen gesamt", preview.payments_total],
    ];
    rows.push([]);
    rows.push(["Zahlungsarten"]);
    for (const m of preview.totals_by_method) {
      rows.push([paymentMethodLabel(m.payment_method), m.total_amount, String(m.count)]);
    }
    const counts = isClosed
      ? normalizeDenominationCounts(
          selectedClosing?.snapshot?.cash_denomination_counts as Record<string, unknown> | undefined,
        )
      : denominationCounts;
    if (hasDenominationCounts(counts)) {
      rows.push([]);
      rows.push(["Kassensturz Stückelung", "Anzahl", "Summe"]);
      for (const denomination of EUR_CASH_DENOMINATIONS) {
        const key = denominationKey(denomination.value);
        const count = counts[key] ?? 0;
        if (count > 0) {
          rows.push([
            denomination.label,
            String(count),
            String(Math.round(denomination.value * count * 100) / 100),
          ]);
        }
      }
    }
    downloadCsv(`tagesabschluss_${closingDate}.csv`, rows);
  }

  const isClosed = preview?.is_closed ?? false;
  const display = selectedClosing ?? preview;
  const cashReady = toAmountNumber(cashCounted) != null;
  const cardReady = toAmountNumber(cardSettlement) != null;
  const reasonReady = !hasDifference || differenceReason.trim().length > 0;
  const canClose = !isClosed && cashReady && cardReady && reasonReady && !loading && !closing;

  const shownCashDiff =
    isClosed && selectedClosing ? toAmountNumber(selectedClosing.cash_difference) : cashDifference;
  const shownCardDiff =
    isClosed && selectedClosing ? toAmountNumber(selectedClosing.card_difference) : cardDifference;

  const closeLabel = !cashReady
    ? "Erst die Kasse zählen"
    : !cardReady
      ? "Terminalbetrag eintragen"
      : !reasonReady
        ? "Begründung ergänzen"
        : "Tagesabschluss festschreiben";

  return (
    <div className="space-y-4 max-lg:pb-20">
      <div className="flex flex-col gap-3 rounded-ios border border-separator bg-card p-3 shadow-ios sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Vorheriger Tag"
            onClick={() => selectDate(shiftIsoDate(closingDate, -1))}
          >
            ‹
          </Button>
          <label className="block">
            <span className="sr-only">Geschäftstag</span>
            <input
              type="date"
              className="eh-input"
              value={closingDate}
              onChange={(e) => selectDate(e.target.value)}
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Nächster Tag"
            disabled={closingDate >= today}
            onClick={() => selectDate(shiftIsoDate(closingDate, 1))}
          >
            ›
          </Button>
          {closingDate !== today ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => selectDate(today)}>
              Heute
            </Button>
          ) : null}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-label">{formatLongDay(closingDate)}</p>
            <p className="text-xs text-label-secondary">
              {isClosed ? "Festgeschrieben" : "Noch offen"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={exportClosingCsv}
            disabled={!display || loading}
          >
            CSV
          </Button>
          <Link
            to="/cashbook"
            className="inline-flex h-8 items-center rounded-lg px-3 text-sm font-medium text-accent hover:bg-fill-tertiary"
          >
            Kassenjournal
          </Link>
        </div>
      </div>

      {error ? (
        <p className="rounded-xl border border-status-red/40 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="rounded-xl border border-status-green/40 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          {success}
        </p>
      ) : null}

      {preview && preview.closing_date === closingDate ? (
        <>
          {isClosed ? (
            <div className="rounded-xl border border-status-green/40 bg-status-green/10 px-4 py-3">
              <p className="text-sm font-medium text-status-green">
                Tagesabschluss festgeschrieben
                {selectedClosing?.closed_by ? ` von ${selectedClosing.closed_by}` : ""}
                {selectedClosing?.closed_at
                  ? ` am ${formatDateTimeShort(selectedClosing.closed_at)}`
                  : ""}
              </p>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <article
              className={clsx(
                "rounded-ios border bg-card p-4 shadow-ios",
                !cashReady && "border-separator",
                cashReady && !amountsDiffer(shownCashDiff) && "border-status-green/40",
                cashReady && amountsDiffer(shownCashDiff) && (shownCashDiff ?? 0) < 0 && "border-status-red/40",
                cashReady && amountsDiffer(shownCashDiff) && (shownCashDiff ?? 0) > 0 && "border-status-orange/40",
              )}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">Kasse</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-label-secondary">Soll</p>
                  <p className="text-2xl font-semibold tabular-nums tracking-tight text-label">
                    {formatCurrency(preview.cash_expected, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-label-secondary">Gezählt</p>
                  <p className="text-2xl font-semibold tabular-nums tracking-tight text-label">
                    {cashReady ? formatCurrency(cashCounted, currency) : "—"}
                  </p>
                </div>
              </div>
              <p
                className={clsx(
                  "mt-3 text-sm font-medium",
                  !cashReady && "text-label-secondary",
                  cashReady && !amountsDiffer(shownCashDiff) && "text-status-green",
                  cashReady && amountsDiffer(shownCashDiff) && (shownCashDiff ?? 0) < 0 && "text-status-red",
                  cashReady && amountsDiffer(shownCashDiff) && (shownCashDiff ?? 0) > 0 && "text-status-orange",
                )}
                aria-live="polite"
              >
                {cashDiffSentence(shownCashDiff, currency, cashReady)}
              </p>
            </article>

            <article
              className={clsx(
                "rounded-ios border bg-card p-4 shadow-ios",
                !cardReady && "border-separator",
                cardReady && !amountsDiffer(shownCardDiff) && "border-status-green/40",
                cardReady && amountsDiffer(shownCardDiff) && (shownCardDiff ?? 0) < 0 && "border-status-red/40",
                cardReady && amountsDiffer(shownCardDiff) && (shownCardDiff ?? 0) > 0 && "border-status-orange/40",
              )}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">Karte</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-label-secondary">System</p>
                  <p className="text-2xl font-semibold tabular-nums tracking-tight text-label">
                    {formatCurrency(preview.card_expected, currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-label-secondary">Terminal</p>
                  <p className="text-2xl font-semibold tabular-nums tracking-tight text-label">
                    {cardReady ? formatCurrency(cardSettlement, currency) : "—"}
                  </p>
                </div>
              </div>
              <p
                className={clsx(
                  "mt-3 text-sm font-medium",
                  !cardReady && "text-label-secondary",
                  cardReady && !amountsDiffer(shownCardDiff) && "text-status-green",
                  cardReady && amountsDiffer(shownCardDiff) && (shownCardDiff ?? 0) < 0 && "text-status-red",
                  cardReady && amountsDiffer(shownCardDiff) && (shownCardDiff ?? 0) > 0 && "text-status-orange",
                )}
              >
                {isClosed && !cardReady
                  ? "Kein Terminalbetrag hinterlegt."
                  : cardDiffSentence(shownCardDiff, currency, cardReady)}
              </p>
            </article>
          </div>

          <form
            id="daily-closing-form"
            onSubmit={handleClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
                e.preventDefault();
              }
            }}
            className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start"
          >
            <div className="order-1 space-y-4 lg:order-none lg:col-start-1 lg:row-start-1">
              <Card
                title="1 · Kasse zählen"
                description="Stück für Stück mit + und − erfassen. Der gezählte Bestand steht unten."
                actions={
                  !isClosed ? (
                    <div className="flex flex-wrap gap-1">
                      {!useManualCashEntry ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDenominationCounts(emptyDenominationCounts())}
                        >
                          Zählung löschen
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUseManualCashEntry((prev) => !prev);
                          if (!useManualCashEntry) {
                            setDenominationCounts(emptyDenominationCounts());
                          }
                        }}
                      >
                        {useManualCashEntry ? "Stückelung zählen" : "Betrag direkt eingeben"}
                      </Button>
                    </div>
                  ) : null
                }
              >
                {useManualCashEntry ? (
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Gezählter Kassenbestand</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="eh-input w-full max-w-xs text-lg font-semibold"
                      value={cashCounted}
                      onChange={(e) => setCashCounted(e.target.value)}
                      disabled={isClosed}
                      required
                    />
                    <p className="mt-2 text-sm text-label-secondary">
                      Soll {formatCurrency(preview.cash_expected, currency)}
                      {cashReady ? ` · ${cashDiffSentence(shownCashDiff, currency, true)}` : ""}
                    </p>
                  </label>
                ) : (
                  <CashDenominationGrid
                    counts={denominationCounts}
                    currency={currency}
                    total={isClosed ? (toAmountNumber(cashCounted) ?? 0) : denominationTotal}
                    expected={toAmountNumber(preview.cash_expected)}
                    disabled={isClosed}
                    onCountChange={handleDenominationCountChange}
                  />
                )}
              </Card>

              <Card
                title="2 · Karte prüfen"
                description="Den Betrag vom Tagesabschluss-Beleg des Terminals eintragen."
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">
                      Terminalbetrag · System {formatCurrency(preview.card_expected, currency)}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="eh-input w-full text-lg font-semibold"
                      value={cardSettlement}
                      onChange={(e) => setCardSettlement(e.target.value)}
                      disabled={isClosed}
                      placeholder={preview.card_expected}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Belegnummer (optional)</span>
                    <input
                      type="text"
                      className="eh-input w-full"
                      value={cardReference}
                      onChange={(e) => setCardReference(e.target.value)}
                      disabled={isClosed}
                      placeholder="z. B. Batch-Nr."
                    />
                  </label>
                </div>
                {!isClosed && toAmountNumber(cardSettlement) !== toAmountNumber(preview.card_expected) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setCardSettlement(preview.card_expected)}
                  >
                    Beleg stimmt ({formatCurrency(preview.card_expected, currency)})
                  </Button>
                ) : null}
              </Card>

              {hasDifference && !isClosed ? (
                <div className="rounded-ios border border-status-orange/40 bg-status-orange/10 p-4">
                  <label className="block text-sm">
                    <span className="mb-1 block font-semibold text-label">
                      Warum weicht der Abschluss ab?
                    </span>
                    <textarea
                      className="eh-input mt-2 w-full min-h-[4.5rem] bg-card"
                      value={differenceReason}
                      onChange={(e) => setDifferenceReason(e.target.value)}
                      rows={3}
                      placeholder="Kurz notieren, z. B. Wechselgeld falsch herausgegeben"
                    />
                  </label>
                </div>
              ) : null}

              {isClosed && selectedClosing?.difference_reason ? (
                <div className="rounded-ios border border-separator bg-card px-4 py-3 text-sm shadow-ios">
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Begründung
                  </p>
                  <p className="mt-1 text-label">{selectedClosing.difference_reason}</p>
                </div>
              ) : null}

              {!isClosed ? (
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Notiz zum Tag (optional)</span>
                  <textarea
                    className="eh-input w-full min-h-[3.5rem]"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Nur wenn etwas Besonderes war"
                  />
                </label>
              ) : selectedClosing?.notes ? (
                <p className="text-sm text-label-secondary">Notiz: {selectedClosing.notes}</p>
              ) : null}
            </div>

            <aside className="order-2 space-y-4 lg:sticky lg:top-[calc(var(--eh-app-header-height)+var(--eh-safe-top)+0.75rem)] lg:order-none lg:col-start-2 lg:row-start-1 lg:self-start">
              <Card
                title="Soll-Bestand"
                description={
                  preview.prior_closing_date
                    ? `Übertrag vom ${formatIsoDateShort(preview.prior_closing_date)}`
                    : "Noch kein Vortag – Anfangsbestand ist 0,00 €"
                }
              >
                <LedgerLine label="Anfangsbestand" value={preview.opening_balance} currency={currency} />
                <LedgerLine label="+ Barzahlungen" value={preview.cash_receipts} currency={currency} />
                <LedgerLine label="+ Einlagen" value={preview.cash_deposits} currency={currency} />
                <LedgerLine label="− Entnahmen und Ausgaben" value={preview.cash_withdrawals} currency={currency} />
                <LedgerLine label="Soll in der Kasse" value={preview.cash_expected} currency={currency} emphasis />

                {preview.cash_movements.length > 0 ? (
                  <ul className="mt-3 divide-y divide-separator border-t border-separator">
                    {preview.cash_movements.map((m) => (
                      <li key={m.id} className="flex items-start justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-label">
                            {MOVEMENT_TYPE_LABELS[m.movement_type]}
                          </p>
                          <p className="truncate text-xs text-label-secondary">{m.reason}</p>
                        </div>
                        <p
                          className={clsx(
                            "shrink-0 text-sm font-semibold tabular-nums",
                            m.direction === "in" ? "text-status-green" : "text-status-red",
                          )}
                        >
                          {m.direction === "in" ? "+" : "−"}
                          {formatCurrency(m.amount, currency)}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {!isClosed ? (
                  <div className="mt-3 border-t border-separator pt-3">
                    {movementFormOpen ? (
                      <div className="space-y-2">
                        <label className="block text-sm">
                          <span className="mb-1 block text-label-secondary">Art</span>
                          <select
                            className="eh-input w-full"
                            value={movementType}
                            onChange={(e) => setMovementType(e.target.value as CashMovementType)}
                          >
                            {CASH_MOVEMENT_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm">
                          <span className="mb-1 block text-label-secondary">Betrag</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="eh-input w-full"
                            value={movementAmount}
                            onChange={(e) => setMovementAmount(e.target.value)}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="mb-1 block text-label-secondary">Buchungstext</span>
                          <input
                            type="text"
                            className="eh-input w-full"
                            value={movementReason}
                            onChange={(e) => setMovementReason(e.target.value)}
                            placeholder="z. B. Wechselgeld"
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="mb-1 block text-label-secondary">Referenz (optional)</span>
                          <input
                            type="text"
                            className="eh-input w-full"
                            value={movementReference}
                            onChange={(e) => setMovementReference(e.target.value)}
                          />
                        </label>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            type="button"
                            size="sm"
                            loading={addingMovement}
                            disabled={!movementAmount || !movementReason.trim()}
                            onClick={() => {
                              void handleAddMovement();
                            }}
                          >
                            Erfassen
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setMovementFormOpen(false)}
                          >
                            Schließen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setMovementFormOpen(true)}
                      >
                        Einlage oder Entnahme
                      </Button>
                    )}
                  </div>
                ) : null}
              </Card>

              {!isClosed ? (
                <div className="hidden lg:block">
                  <Card title="3 · Festschreiben">
                    <p className="text-sm text-label-secondary">
                      {canClose
                        ? "Alles geprüft. Danach lässt sich dieser Tag nicht mehr ändern."
                        : closeLabel}
                    </p>
                    <Button
                      type="submit"
                      size="lg"
                      className="mt-3 w-full"
                      loading={closing}
                      disabled={!canClose}
                    >
                      {closeLabel}
                    </Button>
                  </Card>
                </div>
              ) : null}

              {preview.by_cashier.length > 0 ? (
                <Card title="Kassierer">
                  <ul className="space-y-2">
                    {preview.by_cashier.map((c) => (
                      <li key={c.recorded_by} className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 truncate text-sm text-label">{c.recorded_by}</span>
                        <span className="shrink-0 text-right text-sm tabular-nums text-label">
                          <span className="font-semibold">{formatCurrency(c.total_amount, currency)}</span>
                          <span className="mt-0.5 block text-xs text-label-secondary">
                            davon bar {formatCurrency(c.cash_amount, currency)} · {c.count}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ) : null}
            </aside>
          </form>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {PAYMENT_METHODS.map((method) => {
              const row = preview.totals_by_method.find((t) => t.payment_method === method);
              return (
                <div key={method} className="rounded-xl border border-separator bg-card px-3 py-2.5 shadow-ios">
                  <p className="text-xs text-label-secondary">{PAYMENT_METHOD_LABELS[method]}</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-label">
                    {formatCurrency(row?.total_amount ?? "0", currency)}
                  </p>
                  <p className="text-xs text-label-secondary">
                    {row?.count ?? 0} Zahlung{(row?.count ?? 0) === 1 ? "" : "en"}
                  </p>
                </div>
              );
            })}
          </div>

          {!isClosed ? (
            <div
              className="fixed inset-x-0 z-30 border-t border-separator bg-card/95 px-3 py-2.5 backdrop-blur-md lg:hidden"
              style={{ bottom: "calc(var(--eh-bottom-nav-height) + env(safe-area-inset-bottom, 0px))" }}
            >
              <div className="mx-auto flex max-w-7xl items-center gap-3">
                <p className="min-w-0 flex-1 truncate text-sm font-medium text-label">{closeLabel}</p>
                <Button
                  type="submit"
                  form="daily-closing-form"
                  loading={closing}
                  disabled={!canClose}
                >
                  Festschreiben
                </Button>
              </div>
            </div>
          ) : null}

          {history.length > 0 ? (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-label">Letzte Abschlüsse</h2>
              <Table wide isEmpty={false}>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Soll / Gezählt</th>
                    <th>Differenz</th>
                    <th>Karte</th>
                    <th>Festgeschrieben</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((c) => {
                    const diff = Number(c.cash_difference);
                    return (
                      <tr
                        key={c.id}
                        className="cursor-pointer hover:bg-fill-tertiary"
                        onClick={() => selectDate(c.closing_date)}
                      >
                        <td data-label="Datum">{formatIsoDateShort(c.closing_date)}</td>
                        <td className="tabular-nums text-sm" data-label="Soll / Gezählt">
                          {formatCurrency(c.cash_expected, c.currency)} /{" "}
                          {formatCurrency(c.cash_counted, c.currency)}
                        </td>
                        <td
                          className={clsx(
                            "tabular-nums text-sm",
                            diff < -0.001 && "text-status-red",
                            diff > 0.001 && "text-status-orange",
                          )}
                          data-label="Differenz"
                        >
                          {formatCurrency(c.cash_difference, c.currency)}
                        </td>
                        <td className="tabular-nums text-sm" data-label="Karte">
                          {formatCurrency(c.card_expected, c.currency)}
                          {c.card_settlement
                            ? ` / ${formatCurrency(c.card_settlement, c.currency)}`
                            : ""}
                        </td>
                        <td className="text-sm text-label-secondary" data-label="Festgeschrieben">
                          {c.closed_by ?? "—"} · {formatDateTimeShort(c.closed_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </section>
          ) : null}

          <p className="text-xs text-label-secondary">{preview.logic_note}</p>
        </>
      ) : loading ? (
        <div className="space-y-3" aria-busy="true">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="h-32 animate-pulse rounded-ios bg-fill-tertiary" />
            <div className="h-32 animate-pulse rounded-ios bg-fill-tertiary" />
          </div>
          <div className="h-96 animate-pulse rounded-ios bg-fill-tertiary" />
        </div>
      ) : null}
    </div>
  );
}
